/**
 * Typed fetch for the Klunkers API. Attaches the access token, refreshes it
 * once on a 401 (one refresh shared by concurrent requests), validates
 * responses against the shared zod schemas and surfaces the shared error shape.
 */
import { ApiError, type ErrorCode } from '@klunkers/shared'
import type { z } from 'zod'

export const API_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '')

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

/** The request never reached the API (offline, DNS, CORS, server down). */
export class NetworkError extends Error {
  constructor() {
    super('Cannot reach Klunkers right now. Check your signal and try again.')
    this.name = 'NetworkError'
  }
}

export interface TokenProvider {
  accessToken(): string | null
  /** Try to get a new access token. Resolves false if the session has ended. */
  refresh(): Promise<boolean>
}

let tokens: TokenProvider | null = null
export function setTokenProvider(provider: TokenProvider | null) {
  tokens = provider
}

export interface RequestOptions<S extends z.ZodType | undefined> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  schema?: S
  /** Send the access token and retry once after a refresh. Default true. */
  auth?: boolean
  signal?: AbortSignal
}

type Result<S> = S extends z.ZodType ? z.infer<S> : undefined

async function send(
  path: string,
  opts: RequestOptions<z.ZodType | undefined>,
  token: string | null,
) {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (opts.body !== undefined) headers['content-type'] = 'application/json'
  if (token) headers.authorization = `Bearer ${token}`
  try {
    return await fetch(`${API_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new NetworkError()
  }
}

async function toError(res: Response): Promise<ApiRequestError> {
  const body: unknown = await res.json().catch(() => null)
  const parsed = ApiError.safeParse(body)
  if (parsed.success) {
    const { code, message, details } = parsed.data.error
    return new ApiRequestError(res.status, code, message, details)
  }
  return new ApiRequestError(
    res.status,
    res.status >= 500 ? 'internal' : 'validation_failed',
    'Something went wrong.',
  )
}

export async function api<S extends z.ZodType | undefined = undefined>(
  path: string,
  opts: RequestOptions<S> = {},
): Promise<Result<S>> {
  const useAuth = opts.auth !== false
  let res = await send(path, opts, useAuth ? (tokens?.accessToken() ?? null) : null)
  if (res.status === 401 && useAuth && tokens && (await tokens.refresh())) {
    res = await send(path, opts, tokens.accessToken())
  }
  if (!res.ok) throw await toError(res)
  if (res.status === 204 || !opts.schema) return undefined as Result<S>
  return opts.schema.parse(await res.json()) as Result<S>
}

/** A user-facing message for any error thrown by api(). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiRequestError || err instanceof NetworkError) return err.message
  return 'Something went wrong. Try again.'
}
